import type { ReactNode } from 'react'
import { shimmer } from '@/components/shimmer'
import { cn } from '@/lib/utils'

/**
 * THE icon tile: a glyph on its own small surface.
 *
 * One component so every tile in the app answers the pointer the same way. Before
 * this each call site invented its own — the select cards faded a background, the
 * empty states did nothing at all — and an icon that reacts on one screen and sits
 * inert on the next reads as two different products.
 *
 * The standard, in full:
 *
 *   - the TRAVELLING BAND passes along the tile's edge while hovered, the same
 *     band and the same clock as every other edge in the app (the `border` shimmer);
 *   - the SURFACE lifts one step and the GLYPH goes to full strength, both on the
 *     STATE tier rather than the interaction tier. A tile is a bigger colour
 *     change than a label, and at 130ms that change lands as a snap — the extra
 *     70ms is the difference between a jump and a fade;
 *   - direction follows the ground (docs/ui-effects.md §1.2b): the glyph deepens
 *     on paper and brightens on ink, which `text-foreground` gives for free.
 *
 * `band={false}` for a tile inside a surface that ALREADY sweeps — a select card
 * carries its own edge, and two bands on one card read as a glitch rather than as
 * one object.
 */
export function IconTile({
  children,
  size = 'md',
  band = true,
  className,
}: {
  children: ReactNode
  /** `md` is a tile beside a label; `lg` is the standalone one an empty state shows. */
  size?: 'md' | 'lg'
  band?: boolean
  className?: string
}) {
  return (
    <span
      data-slot="icon-tile"
      {...(band ? shimmer({ variant: 'border' }) : {})}
      className={cn(
        'grid shrink-0 place-items-center rounded-md border border-transparent',
        size === 'md' ? 'size-10' : 'size-14 rounded-2xl',
        'bg-muted text-foreground/70',
        // The colour change is the slow one. See the note above: a tile-sized swap
        // at interaction speed reads as a snap.
        'transition-[background-color,color,border-color] duration-[var(--motion-state)] ease-[var(--motion-ease)]',
        'group-hover:bg-foreground/10 group-hover:text-foreground',
        'hover:bg-foreground/10 hover:text-foreground',
        'motion-reduce:transition-none',
        className,
      )}
    >
      {children}
    </span>
  )
}

/**
 * The "nothing here yet" panel a table shows in place of rows.
 *
 * Was duplicated character-for-character across the analyses and reels tables, so
 * a change to one silently left the other behind. The icon is an `IconTile`, which
 * is what gives the empty state the same band and the same hover as every other
 * icon surface.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon: ReactNode
  title: string
  description: string
  /** The one thing to do from here — usually the create button. */
  action?: ReactNode
}) {
  return (
    <div className="mx-auto flex max-w-sm flex-col items-center gap-4 text-center">
      <IconTile size="lg" className="border-border bg-muted/40">
        {icon}
      </IconTile>
      <div className="space-y-1">
        <p className="font-medium">{title}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {action}
    </div>
  )
}
